// Components/User/MiniCRM/DealList.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";
import Pagination from "../../../Helpers/Pagination";
import "../../../Assets/css/style.css";

const STATUS_META = {
  PENDING: { label: "Pending", cls: "crm-badge--pending" },
  ACCEPTED: { label: "Accepted", cls: "crm-badge--accepted" },
  REJECTED: { label: "Rejected", cls: "crm-badge--rejected" },
  COMPLETED: { label: "Completed", cls: "crm-badge--completed" },
};

const DealList = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((s) => s.customer?.customer?.user);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 10;

  const fetchDeals = async () => {
    setLoading(true);
    try {
      const res = await Handler({
        method: "GET",
        url: `/mini_crm/deals_list.php?page=${page}&limit=${LIMIT}&q=${query}`,
      });
      if (res.success) {
        setList(res.data?.list || []);
        setTotal(res.data?.pagination?.total || 0);
      } else {
        toast.error(res.message || "Failed to load deals");
      }
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDeals(); }, [page, query]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const getOtherParty = (deal) => {
    const isInitiator = parseInt(deal.initiator_id) === parseInt(currentUser?.id);
    return isInitiator
      ? { name: deal.receiver_name, role: "Receiver" }
      : { name: deal.initiator_name, role: "Initiator" };
  };

  return (
    <MainLayout>
      <FeedLayout>
        <div className="crm-page">

          {/* Header */}
          <div className="crm-header">
            <div>
              <h1 className="crm-title">Mini CRM</h1>
              <p className="crm-subtitle">Manage your vendor deals and negotiations</p>
            </div>
            <button
              className="crm-btn crm-btn--primary"
              onClick={() => navigate("/user/crm/create")}
            >
              <i className="bx bx-plus" /> New Deal
            </button>
          </div>

          {/* Search */}
          <form className="crm-search-row" onSubmit={handleSearch}>
            <div className="crm-search-wrap">
              <i className="bx bx-search crm-search-icon" />
              <input
                className="crm-search-input"
                placeholder="Search by name or title..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button className="crm-btn crm-btn--outline" type="submit">Search</button>
          </form>

          {/* List */}
          {loading ? (
            <div className="crm-loading">
              <div className="crm-spinner" />
              <p>Loading deals…</p>
            </div>
          ) : list.length === 0 ? (
            <div className="crm-empty">
              <i className="bx bx-briefcase-alt-2" />
              <h3>No Deals Found</h3>
              <p>Start by creating a new deal with a vendor.</p>
            </div>
          ) : (
            <div className="crm-list">
              {list.map((deal) => {
                const other = getOtherParty(deal);
                const meta = STATUS_META[deal.status] || STATUS_META.PENDING;
                const isInit = parseInt(deal.initiator_id) === parseInt(currentUser?.id);
                const canRespond = !isInit && deal.status === "PENDING";
                const canComplete = isInit && deal.status === "ACCEPTED";
                const canChat = deal.status === "ACCEPTED";

                return (
                  <div key={deal.id} className="crm-deal-card">
                    <div className="crm-deal-left">
                      <div className="crm-deal-avatar">
                        {other.name?.slice(0, 2).toUpperCase() || "??"}
                      </div>
                      <div className="crm-deal-info">
                        <div className="crm-deal-name">
                          {other.name}
                          <span className="crm-deal-role">{other.role}</span>
                        </div>
                        {deal.title && (
                          <div className="crm-deal-title">{deal.title}</div>
                        )}
                        {deal.description && (
                          <div className="crm-deal-desc">{deal.description}</div>
                        )}
                        <div className="crm-deal-date">
                          <i className="bx bx-calendar" />
                          {new Date(deal.created_at).toLocaleDateString("en-IN", {
                            day: "2-digit", month: "short", year: "numeric",
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="crm-deal-right">
                      <span className={`crm-badge ${meta.cls}`}>{meta.label}</span>
                      <div className="crm-deal-actions">
                        {canChat && (
                          <button
                            className="crm-act-btn crm-act-btn--chat"
                            onClick={() => navigate(`/user/crm/chat/${deal.id}`)}
                          >
                            <i className="bx bx-message-dots" /> Chat
                          </button>
                        )}
                        {canRespond && (
                          <button
                            className="crm-act-btn crm-act-btn--respond"
                            onClick={() => navigate(`/user/crm/respond/${deal.id}`)}
                          >
                            <i className="bx bx-check-shield" /> Respond
                          </button>
                        )}
                        {canComplete && (
                          <button
                            className="crm-act-btn crm-act-btn--complete"
                            onClick={() => navigate(`/user/crm/complete/${deal.id}`, {
                              state: {
                                vendorId: deal.receiver_id,
                                vendorName: deal.receiver_name
                              }
                            })}
                          >
                            <i className="bx bx-badge-check" /> Complete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Pagination
            page={page}
            total={total}
            limit={LIMIT}
            onPrev={() => setPage((p) => p - 1)}
            onNext={() => setPage((p) => p + 1)}
          />

        </div>
      </FeedLayout>
    </MainLayout>
  );
};

export default DealList;